/**
 * AI Provider Configuration
 * Static UI metadata for supported AI providers and modalities.
 * Model data comes from Registry API via useRegistryProviders() hooks.
 */

import type { AIModality, ModalityConfig, ProviderConfig } from './types';

export const PROVIDERS: ProviderConfig[] = [
  // Chat & Image Providers
  {
    id: 'openai',
    name: 'OpenAI',
    modalities: ['chat', 'image', 'stt', 'embedding', 'rowMatching'],
    group: 'chatAndImage',
    keyPlaceholder: 'sk-proj-...',
    keyPrefix: 'sk-',
    docsUrl: 'https://platform.openai.com/api-keys',
    signupUrl: 'https://auth.openai.com/create-account',
    description: 'Chat, Image Generation, Voice Transcription, Embeddings',
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    modalities: ['chat', 'rowMatching'],
    group: 'chatAndImage',
    keyPlaceholder: 'sk-ant-...',
    keyPrefix: 'sk-ant-',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    signupUrl: 'https://platform.claude.com/login',
    description: 'Chat (Claude models)',
  },
  {
    id: 'google',
    name: 'Google AI',
    modalities: ['chat', 'image', 'embedding', 'rowMatching'],
    group: 'chatAndImage',
    keyPlaceholder: 'AIza...',
    keyPrefix: 'AIza',
    docsUrl: 'https://aistudio.google.com/apikey',
    signupUrl: 'https://ai.google.dev/gemini-api/docs/api-key',
    description: 'Chat, Image Generation, Embeddings',
  },
  {
    id: 'fal',
    name: 'Fal.ai',
    modalities: ['image'],
    group: 'chatAndImage',
    keyPlaceholder: 'fal-...',
    keyPrefix: 'fal-',
    docsUrl: 'https://fal.ai/dashboard/keys',
    signupUrl: 'https://fal.ai/',
    description: 'Image Generation (Flux models)',
  },
  // Web Search Providers
  {
    id: 'exa',
    name: 'Exa',
    modalities: ['webSearch'],
    group: 'webSearch',
    keyPlaceholder: 'exa-...',
    docsUrl: 'https://dashboard.exa.ai/api-keys',
    signupUrl: 'https://exa.ai/',
    description: 'Web Search',
  },
  {
    id: 'tavily',
    name: 'Tavily',
    modalities: ['webSearch'],
    group: 'webSearch',
    keyPlaceholder: 'tvly-...',
    keyPrefix: 'tvly-',
    docsUrl: 'https://app.tavily.com/home',
    signupUrl: 'https://www.tavily.com/',
    description: 'Web Search',
  },
];

export const MODALITIES: ModalityConfig[] = [
  {
    id: 'chat',
    label: 'Chat / Ask AI',
    description: 'Used for AI chat, writing assistance, and Q&A',
    userLevel: true,
    workspaceLevel: true,
  },
  {
    id: 'image',
    label: 'Image Generation',
    description: 'Used for AI image generation',
    userLevel: true,
    workspaceLevel: true,
  },
  {
    id: 'stt',
    label: 'Voice Transcription',
    description: 'Used for voice input and transcription',
    userLevel: true,
    workspaceLevel: true,
  },
  {
    id: 'webSearch',
    label: 'Web Search',
    description: 'Used for searching the web within AI conversations',
    userLevel: true,
    workspaceLevel: true,
  },
  {
    id: 'embedding',
    label: 'Embeddings',
    description: 'Used for semantic search and document indexing',
    userLevel: false,
    workspaceLevel: true,
  },
];

export function getProvidersByGroup(
  group: 'chatAndImage' | 'webSearch'
): ProviderConfig[] {
  return PROVIDERS.filter(p => p.group === group);
}

export function getProviderById(id: string): ProviderConfig | undefined {
  return PROVIDERS.find(p => p.id === id);
}

export function getModalitiesForLevel(
  level: 'user' | 'workspace'
): ModalityConfig[] {
  return MODALITIES.filter(m =>
    level === 'user' ? m.userLevel : m.workspaceLevel
  );
}

export function getProvidersForModality(
  modality: AIModality
): ProviderConfig[] {
  return PROVIDERS.filter(p => p.modalities.includes(modality));
}
